import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { useAdminAuth } from "./context/AdminAuthContext";
import { useData } from "./context/DataContext";
import { Loader2 } from "lucide-react";

// Auth pages
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import VerifyCode from "./pages/VerifyCode";
import PhoneCapture from "./pages/PhoneCapture";
import PhoneVerify from "./pages/PhoneVerify";
import Onboarding from "./pages/Onboarding";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPasswordVerify from "./pages/ResetPasswordVerify";
import ResetPasswordNew from "./pages/ResetPasswordNew";

// App pages
import Layout from "./components/Layout";
import OfflineAlert from "./components/OfflineAlert";
import { Analytics } from "@vercel/analytics/react";
import Dashboard from "./pages/Dashboard";
import Properties from "./pages/Properties";
import PropertyDetail from "./pages/PropertyDetail";
import Tenants from "./pages/Tenants";
import RentStatus from "./pages/RentStatus";
import AiCalls from "./pages/AiCalls";
import SmsReminders from "./pages/SmsReminders";
import Reports from "./pages/Reports";
import Statement from "./pages/Statement";
import Settings from "./pages/Settings";
import Billing from "./pages/Billing";
import UtilityBills from "./pages/UtilityBills";

// Admin pages
import AdminLayout from "./pages/admin/AdminLayout";
import AdminVerify2FA from "./pages/admin/AdminVerify2FA";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import AdminUserEvents from "./pages/admin/AdminUserEvents";
import AdminChangePassword from "./pages/admin/AdminChangePassword";
import AdminAuditLog from "./pages/admin/AdminAuditLog";

// Landing & SEO pages
import LandingLayout from "./landing/LandingLayout";
import Landing from "./pages/Landing";
import FeaturesPage from "./pages/FeaturesPage";
import PricingPage from "./pages/PricingPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import BlogPage from "./pages/BlogPage";
import BlogPost from "./pages/BlogPost";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import Download from "./pages/Download";

function AuthLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-950">
      <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoading />;
  if (!user) return <Navigate to="/signin" replace />;
  return children;
}

function RequirePhone({ children }) {
  const { user } = useAuth();
  if (user && user.needsPhone && !user.phone?.e164) {
    return <Navigate to="/phone" replace />;
  }
  return children;
}

function RequireOnboarded({ children }) {
  const { user } = useAuth();
  const { loading: dataLoading, loadedUserId, onboarded, properties, tenants } = useData();
  // Show spinner if data is loading OR data is stale (from a different/no user)
  if (dataLoading || (user && loadedUserId !== user.id)) return <AuthLoading />;
  if (onboarded) return children;
  if (properties.length > 0 || tenants.length > 0) return children;
  return <Navigate to="/onboarding" replace />;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoading />;
  if (user) return <Navigate to="/app/dashboard" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { admin, loading } = useAdminAuth();
  if (loading) return <AuthLoading />;
  if (!admin) return <Navigate to="/signin" replace />;
  return children;
}

export default function App() {
  return (
    <>
      <OfflineAlert />
      <Analytics />
    <Routes>
      {/* Landing & SEO pages (public, with LandingLayout nav + footer) */}
      <Route element={<LandingLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/download" element={<Download />} />
      </Route>

      {/* Auth pages (no nav/footer) */}
      <Route
        path="/signin"
        element={
          <PublicOnly>
            <SignIn />
          </PublicOnly>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicOnly>
            <SignUp />
          </PublicOnly>
        }
      />
      <Route path="/verify" element={<VerifyCode />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/verify" element={<ResetPasswordVerify />} />
      <Route path="/reset-password/new" element={<ResetPasswordNew />} />
      <Route
        path="/phone"
        element={
          <RequireAuth>
            <PhoneCapture />
          </RequireAuth>
        }
      />
      <Route
        path="/phone/verify"
        element={
          <RequireAuth>
            <PhoneVerify />
          </RequireAuth>
        }
      />
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <RequirePhone>
              <Onboarding />
            </RequirePhone>
          </RequireAuth>
        }
      />

      {/* Protected app pages */}
      <Route
        path="/app"
        element={
          <RequireAuth>
            <RequirePhone>
              <RequireOnboarded>
                <Layout />
              </RequireOnboarded>
            </RequirePhone>
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="properties" element={<Properties />} />
        <Route path="properties/:id" element={<PropertyDetail />} />
        <Route path="tenants" element={<Tenants />} />
        <Route path="rent" element={<RentStatus />} />
        <Route path="utilities" element={<UtilityBills />} />
        <Route path="calls" element={<AiCalls />} />
        <Route path="sms" element={<SmsReminders />} />
        <Route path="reports" element={<Reports />} />
        <Route path="reports/statement" element={<Statement />} />
        <Route path="billing" element={<Billing />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* Admin 2FA page — reached only via /signin forking server-side.
          Not linked from anywhere; the user has to enter the admin email. */}
      <Route path="/admin/verify-2fa" element={<AdminVerify2FA />} />

      {/* Admin console */}
      <Route
        path="/admin"
        element={
          <RequireAdmin>
            <AdminLayout />
          </RequireAdmin>
        }
      >
        <Route index element={<Navigate to="/admin/users" replace />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="users/:id" element={<AdminUserDetail />} />
        <Route path="users/:id/events" element={<AdminUserEvents />} />
        <Route path="change-password" element={<AdminChangePassword />} />
        <Route path="audit-log" element={<AdminAuditLog />} />
      </Route>

      {/* Legacy redirects */}
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/properties" element={<Navigate to="/app/properties" replace />} />
      <Route path="/tenants" element={<Navigate to="/app/tenants" replace />} />
      <Route path="/rent" element={<Navigate to="/app/rent" replace />} />
      <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
      <Route path="/reports" element={<Navigate to="/app/reports" replace />} />
      <Route path="/calls" element={<Navigate to="/app/calls" replace />} />
      <Route path="/sms" element={<Navigate to="/app/sms" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
